import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProjectFolderConnection } from "../models/projects/ProjectSettingsResponse";
import { ProjectFileNode } from "../models/shared/ProjectFileNode";
import { SharePointDriveLocation } from "../models/projects/SharePointFolderItem";
import { SharePointAuthServiceFactory } from "../services/SharePoint/SharePointAuthServiceFactory";
import { SharePointGraphError, SharePointGraphService } from "../services/SharePoint/SharePointGraphService";
import { Log } from "../services/Logger";
import { ProjectFolderErrorKind } from "./useProjectFolderSelection";

/** Errors the Project Files browser can raise: the NPC hook's kinds plus SharePoint sign-in/browsing. */
export type ProjectFilesErrorKind =
    | ProjectFolderErrorKind
    | "sharePointAuth"
    | "sharePointForbidden"
    | "sharePointNotConfigured"
    | "sharePointLoad";

/** Maps a typed Graph failure to the banner kind the dialog shows. */
export function toSharePointErrorKind(error: unknown): ProjectFilesErrorKind {
    if (error instanceof SharePointGraphError) {
        if (error.reason === "forbidden") return "sharePointForbidden";
        if (error.reason === "notConfigured") return "sharePointNotConfigured";
        return "sharePointLoad";
    }
    return "sharePointAuth";
}

/** A SharePoint root the user has signed in to and resolved, ready to browse. */
export interface ActiveSharePointConnection {
    connection: ProjectFolderConnection;
    root: SharePointDriveLocation;
    graphService: SharePointGraphService;
}

const SHAREPOINT_ROOT_NRN_PREFIX = "nrn:file:folder:sharepoint:";

/** Synthetic NRN for a SharePoint root folder; keyed by connection id so same-named roots never collide. */
export function buildSharePointRootNrn(connectionId: string): string {
    return `${SHAREPOINT_ROOT_NRN_PREFIX}${connectionId}`;
}

export function toSharePointRootNode(connection: ProjectFolderConnection): ProjectFileNode {
    return {
        id: buildSharePointRootNrn(connection.id),
        name: connection.name,
        type: "folder",
        source: "sharepoint",
        connectionId: connection.id,
    };
}

export interface UseSharePointRootFoldersParams {
    /** SharePoint Online connections from the selected project's settings (already filtered). */
    connections: ProjectFolderConnection[];
    sharePointAuthServiceFactory?: SharePointAuthServiceFactory;
    onError?: (kind: ProjectFilesErrorKind) => void;
    /** Changing either resets navigation and discards in-flight sign-ins. */
    projectNrn?: string;
    resetKey?: number;
}

export interface UseSharePointRootFoldersResult {
    /** One folder node per connection, to merge into the NPC root listing. */
    rootNodes: ProjectFileNode[];
    /** The SharePoint root the user has navigated into, once signed in and resolved. */
    activeConnection: ActiveSharePointConnection | null;
    /** Connection whose sign-in/root resolution is in flight (row spinner). */
    authenticatingConnectionId: string | null;
    isSharePointNode: (node: ProjectFileNode) => boolean;
    /** Signs in to the node's connection (NAA or Office Dialog fallback), resolves its root, then navigates into it. */
    enter: (node: ProjectFileNode) => void;
    exit: () => void;
}

/**
 * Overlays the project's SharePoint Online connections onto the NPC root listing as folders.
 * `/v1/files` does not return SharePoint roots, so they are synthesised from project settings.
 * Clicking one acquires a Graph token for that connection's Entra app/tenant and resolves the
 * folder URL to a drive item; `useSharePointFolderBrowsing` then lists its contents.
 */
export function useSharePointRootFolders(params: UseSharePointRootFoldersParams): UseSharePointRootFoldersResult {
    const { connections, sharePointAuthServiceFactory, onError, projectNrn, resetKey } = params;
    const [activeConnection, setActiveConnection] = useState<ActiveSharePointConnection | null>(null);
    const [authenticatingConnectionId, setAuthenticatingConnectionId] = useState<string | null>(null);
    // Bumped on reset/unmount so a late sign-in result can't navigate into a stale project.
    const generationRef = useRef(0);
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    const connectionsById = useMemo(() => new Map(connections.map((c) => [c.id, c])), [connections]);
    const rootNodes = useMemo(() => connections.map(toSharePointRootNode), [connections]);

    useEffect(() => {
        generationRef.current += 1;
        setActiveConnection(null);
        setAuthenticatingConnectionId(null);
    }, [projectNrn, resetKey]);

    useEffect(
        () => () => {
            generationRef.current += 1;
        },
        []
    );

    const isSharePointNode = useCallback((node: ProjectFileNode) => node.source === "sharepoint", []);

    const enter = useCallback(
        (node: ProjectFileNode) => {
            const connection = node.connectionId ? connectionsById.get(node.connectionId) : undefined;
            if (!connection || !sharePointAuthServiceFactory || authenticatingConnectionId) return;

            const generation = generationRef.current;
            setAuthenticatingConnectionId(connection.id);
            Log.info(
                `useSharePointRootFolders: opening SharePoint connection "${connection.name}" - requesting a Graph token (may be served from cache, refreshed silently, or prompt)`
            );

            sharePointAuthServiceFactory
                .getServices(connection)
                .then(async ({ authService, graphService }) => {
                    await authService.getAccessToken();
                    const root = await graphService.resolveRoot(connection);
                    return { connection, root, graphService };
                })
                .then(
                    (active) => {
                        if (generationRef.current !== generation) return;
                        setActiveConnection(active);
                    },
                    (error) => {
                        if (generationRef.current !== generation) return;
                        Log.error(
                            `useSharePointRootFolders: could not open SharePoint connection "${connection.name}"`,
                            error
                        );
                        onErrorRef.current?.(toSharePointErrorKind(error));
                    }
                )
                .then(() => {
                    if (generationRef.current === generation) setAuthenticatingConnectionId(null);
                });
        },
        [connectionsById, sharePointAuthServiceFactory, authenticatingConnectionId]
    );

    const exit = useCallback(() => setActiveConnection(null), []);

    return { rootNodes, activeConnection, authenticatingConnectionId, isSharePointNode, enter, exit };
}
